/**
 * HealthScores Component
 *
 * Description: A React component that displays health scores for various systems in a user’s health profile.
 * The component fetches data based on a user ID and visualizes the results using circular progress indicators.
 *
 * File Name: HealthScores.tsx
 * Date: 2024-08-26
 */
import React, { useEffect, useState, useRef } from "react";
import ProgressIndicator from "src/Pages/Customer/Progress/ProgressIndicator";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { barColors } from "src/lib/constans";
import SplashScreen from "src/Components/SplashScreen";
import { IHealthScores } from "src/types/GlobalInterfaces";
import HealthScoreIcon from "src/assets/images/healthscorenewicon.png";
// import HealthScoreIcon from "src/assets/images/heartScoreNew.png"
import iconImmunity from "src/assets/images/customer-dashboard/customer-dashboard-icon-immunity.png";
// import iconBlood from "src/assets/images/customer-dashboard/customer-dashboard-icon-blood.png";
import iconBlood from "src/assets/images/newBloodIcon.png";
import iconCardiovascular from "src/assets/images/customer-dashboard/customer-dashboard-icon-cardiovascular.png";
import iconDigestive from "src/assets/images/customer-dashboard/customer-dashboard-icon-digestive.png";
import iconHormones from "src/assets/images/customer-dashboard/customer-dashboard-icon-hormones.png";
import iconLiver from "src/assets/images/customer-dashboard/customer-dashboard-icon-liver.png";
import iconNerves from "src/assets/images/customer-dashboard/customer-dashboard-icon-nerves.png";
import iconRespiratory from "src/assets/images/customer-dashboard/customer-dashboard-icon-respiratory.png";
import iconUrogenital from "src/assets/images/customer-dashboard/customer-dashboard-icon-urogenital.png";
import { CircularProgress } from "@mui/material";
import CartDrawer from "src/Components/AdminComponents/CartDrawer";
import MyHealthProfileScreen from "src/Components/AllTabs/MyHealthProfileScreen";
import { convertToKebabCase } from "src/helpers/helperFunctions";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";

// Configuration for health systems
const healthSystems = [
  { key: "Urogenital System", label: "Urogenital", icon: iconUrogenital },
  { key: "Respiratory System", label: "Respiratory", icon: iconRespiratory },
  { key: "Nervous System", label: "Nerves", icon: iconNerves },
  { key: "Liver System", label: "Liver", icon: iconLiver },
  { key: "Immune System", label: "Immunity", icon: iconImmunity },
  { key: "Digestive System", label: "Digestive", icon: iconDigestive },
  { key: "Hormones System", label: "Hormones", icon: iconHormones },
  {
    key: "Cardiovascular System",
    label: "Cardiovascular",
    icon: iconCardiovascular,
  },
  { key: "Blood System", label: "Blood", icon: iconBlood },
];

const HealthScores: React.FC<{
  id: string;
  isAdmin?: boolean;
  onPress?: () => void;
  parentBoxClassName?: string;
}> = ({ id, isAdmin = false, onPress = () => {}, parentBoxClassName = "" }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [healthScores, setHealthScores] = useState<number>(0);
  const [data, setData] = useState<IHealthScores | null>(null);
  const [activePopupIndex, setActivePopupIndex] = useState<number | null>(null);
  const popupRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { healthProfile } = useSelector((state: RootState) => state.realTimeState);
  const [showModal, setShowModal] = useState<string | null>(null);

  // const checkClickOrNot = (key: keyof IHealthScores): boolean => {
  //     if (data && data[key]) {
  //         return false
  //     } else {
  //         return true
  //     }
  // };

  // const navigateToBioMarker = (): void => {
  //     if (isAdmin) {
  //         onPress();
  //     } else {
  //         navigate("/health-biomarkers");
  //     }
  // };

  const togglePopup = (index: number) =>
    setActivePopupIndex((prev) => (prev === index ? null : index));

  const handleClickOutside = (event: MouseEvent) => {
    if (activePopupIndex !== null && popupRefs.current[activePopupIndex]) {
      const popup = popupRefs.current[activePopupIndex];
      if (popup && !popup.contains(event.target as Node)) {
        setActivePopupIndex(null);
      }
    }
  };

  const [requiredStates, setRequiredStates] = useState<{
    loader: boolean;
    _id: string;
    biomarkerName: string[];
  }>({
    loader: false,
    _id: "",
    biomarkerName: [],
  });
  const fetchByName = async (cateName: string) => {
    setRequiredStates({ loader: true, _id: "", biomarkerName: [] }); // empty prev states
    const { res, err } = await httpRequest<{
      data : {
        _id: string;
        biomarkerNames: string[];
      },
      success: boolean;
    }>({ path: `/biomarker/category/getallbiomarker?name=${cateName}` });
    if (res) {
      const { _id = "", biomarkerNames = [] } = res.data;
      setRequiredStates((prev) => ({ ...prev, _id, biomarkerName : biomarkerNames }));
    } else {
      toast(err, { type: "error" });
    }
    setRequiredStates((prev) => ({ ...prev, loader: false }));
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activePopupIndex]);

  useEffect(() => {
    if (id) {
      (async () => {
        setIsLoader(true);
        const { res, err } = await httpRequest<{
          message: string;
          data: IHealthScores;
        }>({ path: `/healthprofile/main/${id}` });
        if (res) {
          setData(res.data || null);
          let total =
            (res?.data?.["Cardiovascular System"] || 0) +
            (res?.data?.["Respiratory System"] || 0) +
            (res?.data?.["Urogenital System"] || 0) +
            (res?.data?.["Hormones System"] || 0) +
            (res?.data?.["Nervous System"] || 0) +
            (res?.data?.["Liver System"] || 0) +
            (res?.data?.["Immune System"] || 0) +
            (res?.data?.["Blood System"] || 0) +
            (res?.data?.["Digestive System"] || 0);
          total = Math.round(total / 9);
          setHealthScores(total);
        } else {
          toast(err, { type: "error" });
        }
        setIsLoader(false);
      })();
    }
  }, [id, healthProfile]);

  return (
    <>
      {isLoader && <SplashScreen />}
      <CartDrawer isOpen={showModal !== null} onClose={() => setShowModal(null)}>
        <MyHealthProfileScreen
          onBack={() => setShowModal(null)}
          setActiveTab={() => {
            setShowModal(null);
          }}
          onClose={() => setShowModal(null)}
          id={showModal || ""}
          tab="Biomarkers"
        />
      </CartDrawer>
      <div
        className={`parent-box parent-box-bottom-margin ${parentBoxClassName}`}
      >
        <div className="grid gap-2 md:grid-cols-5 grid-cols-2">
          <div className="md:col-span-3 md:row-span-2 col-span-2">
            <div className="dashboard-lg-box dashboard-lg-box-nnew flex h-full items-center justify-center">
              <div className="dashboard-inner-box flex justify-between items-center gap-4">
                <div className="dashboard-inner-box-text">
                  <img
                    className="health-score-boxes-img-first"
                    src={HealthScoreIcon}
                    alt="main-icon"
                  />
                  <h2 className="font-Poppins-Medium text-white font-size-35px">
                    Health score
                  </h2>
                  <p className="font-Poppins-Regular text-white font-size-17px">
                    Your health score reflects the overall performance of your
                    body systems, calculated from key biomarkers. Use it to
                    identify strengths and areas needing attention, guiding you
                    toward better health.
                  </p>
                </div>
                <div className="dashboard-inner-box-progress">
                  {data && (
                    <ProgressIndicator
                      size={170}
                      type="circular"
                      value={healthScores || 0}
                      color={
                        (healthScores || 0) >= 70
                          ? barColors.green
                          : (healthScores || 0) >= 50
                          ? barColors.orange
                          : barColors.red
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          {healthSystems.map((system, index) => (
            <div key={system.key} className="col-span-1">
              <div
                className={`dashboard-sm-box ${
                  data?.[system.key as keyof IHealthScores] &&
                  !isLoader &&
                  "dashboard-sm-box-nodata"
                }`}
                id={`health-score-${convertToKebabCase(system.key)}`}
              >
                <div className={`dashboard-inner-box`}>
                  {!data?.[system.key as keyof IHealthScores] && !isLoader && (
                    <div className="flex justify-center">
                      <div className="black-overlay">
                        <span className="text-white font-size-13px font-Poppins-SemiBold">
                          Activate this chart by adding the required biomarkers
                          through a test package to view your health overview.
                        </span>
                        <button
                          onClick={(e) => {
                            togglePopup(index);
                            e.stopPropagation();
                            fetchByName(system.key);
                          }}
                          className="text-white font-size-13px font-Poppins-Medium"
                          id={`health-score-required-biomarkers-${convertToKebabCase(system.key)}-btn`}
                        >
                          Required Biomarkers
                        </button>
                      </div>
                      {activePopupIndex === index && (
                        <div
                          ref={(el) => (popupRefs.current[index] = el)}
                          className="show-popup-health-score"
                        >
                          <h3 className="theme-color-green font-size-18px font-Poppins-SemiBold">
                            Required Biomarkers
                          </h3>
                          {requiredStates?.loader ? (
                            <div className="text-center">
                              <CircularProgress
                                size={14}
                                style={{ color: "#004540" }}
                              />
                            </div>
                          ) : (
                            <>
                              {requiredStates?.biomarkerName
                                ?.slice(0, 5)
                                .map((label) => (
                                  <button
                                    disabled
                                    key={label}
                                    className="text-white theme-bg-green font-size-13px font-Poppins-Medium"
                                  >
                                    {label}
                                  </button>
                                ))}
                              {requiredStates?.biomarkerName?.length ? (
                                isAdmin ? (
                                  <button
                                    onClick={onPress}
                                    className="font-size-13px block theme-color-green text-center font-Poppins-Medium"
                                    style={{ textAlign: "center" }}
                                    id={`health-score-view-all-${convertToKebabCase(system.key)}-btn`}
                                  >
                                    View All
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setShowModal(requiredStates?._id)}
                                    className="font-size-13px w-full block theme-color-green text-center font-Poppins-Medium"
                                    id={`health-score-view-all-${convertToKebabCase(system.key)}-btn`}
                                  >
                                    View All
                                  </button>
                                )
                              ) : (
                                <p className="text-center">...</p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <img
                    className="health-score-boxes-img"
                    src={system.icon}
                    alt={`${system.label}-icon`}
                  />
                  <p className="font-Poppins-SemiBold font-size-18px">
                    {system.label}
                  </p>
                  {data && (
                    <ProgressIndicator
                      size={70}
                      type="circular"
                      value={Math.floor(
                        data[system.key as keyof IHealthScores] || 0
                      )}
                      color={
                        (data[system.key as keyof IHealthScores] || 0) >= 70
                          ? barColors.green
                          : (data[system.key as keyof IHealthScores] || 0) >= 50
                          ? barColors.orange
                          : barColors.red
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HealthScores;
